import { useState, useRef, useEffect } from 'react'
import { createOcrJobApi, getOcrJobApi, ocrDocType } from '../../../features/ocr/ocrAPI'

export const ocrStatus = {
  idle: 'idle',
  reading: 'reading',
  completed: 'completed',
  failed: 'failed'
}

// 状態を問い合わせる間隔
export const OCR_POLL_INTERVAL_MS = 4000
// これを過ぎても結果が出なければ失敗とする（API 側の時間切れと同じ）
export const OCR_TIMEOUT_MS = 10 * 60 * 1000

/**
 * 読み取り結果のうち、反映する項目で値のあるものを返す（読み取れなかった項目は今の入力を残す）
 * @returns {Array<[string, any]>}
 */
export const pickOcrValues = (values, fields) => fields
  .filter(key => values[key] !== null && values[key] !== undefined && values[key] !== '')
  .map(key => [key, values[key]])

/**
 * 車検証の AI-OCR（受付 → 結果が出るまで問い合わせ）
 *
 * - 新しく読み取りを始めると、前の読み取りの結果は捨てる
 * - 画面を離れたら問い合わせを止める（結果は捨てる）
 * - 成功したときだけ onCompleted(file, values) を呼ぶ。失敗時は何もしない
 * - docType で読み取る範囲を選ぶ（編集画面は車検の更新で変わる項目だけ）
 */
export default function useInspectionOcr ({ onCompleted, docType = ocrDocType.vehicleInspection }) {
  const [status, setStatus] = useState(ocrStatus.idle)
  // 読み取りごとに番号を進め、古い読み取りの結果を無視する
  const runRef = useRef(0)
  const timerRef = useRef(null)
  const onCompletedRef = useRef(onCompleted)
  onCompletedRef.current = onCompleted

  const stop = () => {
    runRef.current += 1
    clearTimeout(timerRef.current)
    timerRef.current = null
  }

  useEffect(() => stop, [])

  const start = async (file) => {
    stop()
    const run = runRef.current
    const isCurrent = () => run === runRef.current
    const deadline = Date.now() + OCR_TIMEOUT_MS
    setStatus(ocrStatus.reading)

    let jobId
    try {
      const response = await createOcrJobApi({ file, docType })
      jobId = response.data.jobId
    } catch (err) {
      isCurrent() && setStatus(ocrStatus.failed)
      return
    }

    const poll = async () => {
      if (!isCurrent()) return
      if (Date.now() >= deadline) {
        setStatus(ocrStatus.failed)
        return
      }
      try {
        const { data } = await getOcrJobApi({ jobId })
        if (!isCurrent()) return
        if (data.status === 'COMPLETED') {
          setStatus(ocrStatus.completed)
          onCompletedRef.current(file, data.values || {})
          return
        }
        if (data.status === 'FAILED') {
          setStatus(ocrStatus.failed)
          return
        }
      } catch (err) {
        if (!isCurrent()) return
        // 受付が見つからない等はやり直しても変わらない。通信エラーは時間切れまで問い合わせを続ける
        const httpStatus = err.response && err.response.status
        if (httpStatus >= 400 && httpStatus < 500) {
          setStatus(ocrStatus.failed)
          return
        }
      }
      timerRef.current = setTimeout(poll, OCR_POLL_INTERVAL_MS)
    }
    timerRef.current = setTimeout(poll, OCR_POLL_INTERVAL_MS)
  }

  return { status, start }
}
